/**
 * Whether the start-project dialog has everything it needs to create the project.
 *
 * Pure and separate from the component for one reason: **two of these four answers have a real id 0**,
 * and a truthiness check on either silently removes a legitimate choice from the product. It has
 * happened twice already in this dialog. Denmark's "Kontor" is building type 0 and its "Custom"
 * calculation type is id 0. A `!!id` there does not fail loudly; it just refuses to let anyone create a
 * Danish office project, and nobody notices until a user says the button does nothing.
 *
 * As a computed inside the SFC that could only be checked by clicking. Here it is a unit test.
 *
 * Project status used to be a fifth answer, and for a while it was the one field asked for no reason
 * of its own — it shaped no step, and the base project row did not carry it — so it moved to the
 * settings page with everything else the create does not need. It came back as an optional field once
 * the base endpoint started accepting it (RTLCA-5661): optional, so it does not belong in this rule —
 * StartProjectModal sends whatever it has without asking canStartProject about it. The project's named
 * models came back with it and are NOT optional: a project needs at least one model to publish into, so
 * one ticked model is the fifth answer here. There is no upper limit.
 */
export type StartProjectAnswers = {
  /** The Country_Enum, or null before one is chosen. A string is enough for this rule. */
  country: string | null;
  projectName: string;
  buildingTypeId: number | null;
  /** Whether a calculation type resolved from the chosen id. The record itself is irrelevant here. */
  hasCalculationType: boolean;
  /**
   * Whether the tenant already has a project with this name, as last answered by the advisory check
   * (RTLCA-5603). A field on the rule rather than an `&&` in the component, so "may this dialog
   * create" stays one unit-tested answer.
   *
   * The check fails OPEN — see useProjectNameAvailability — so a check that could not be made passes
   * false and the create goes ahead, which is what the backend accepts anyway.
   */
  nameTaken: boolean;
  /**
   * Whether the chosen calculation type answers nothing itself — a Custom type, whose row carries no
   * consideration period and no stages — so the dialog has to ask for the three answers below
   * (RTLCA-5595). Created without them, no project_calculation row is ever written and the backend
   * refuses to compose the project at all: unopenable, uneditable, undeletable.
   */
  needsCalculationDetails: boolean;
  /** Only judged when needsCalculationDetails. 0 is a valid answer — the backend rejects only null/negative. */
  considerationPeriod: number | null;
  thresholdLimit: number | null;
  stageCount: number;
  /** How many models are ticked. At least one; there is no maximum. */
  activeModelCount: number;
};

export const canStartProject = (answers: StartProjectAnswers): boolean =>
  !!answers.country &&
  answers.projectName.trim().length > 0 &&
  !answers.nameTaken &&
  answers.buildingTypeId !== null &&
  answers.hasCalculationType &&
  answers.activeModelCount > 0 &&
  (!answers.needsCalculationDetails ||
    (answers.considerationPeriod !== null &&
      answers.considerationPeriod >= 0 &&
      answers.thresholdLimit !== null &&
      answers.thresholdLimit >= 0 &&
      answers.stageCount > 0));
